// frontend/src/pages/GestionUsuarioNuevoModal.jsx

import React, { useState } from 'react';
import { toast } from 'react-toastify';

const GestionUsuarioNuevoModal = ({ grupos, tiposUsuario, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    tipo_usuario_id: '',
    costo_empresa: '',
    grupos_ids: []
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Manejar cambios en inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Toggle grupo
  const handleToggleGrupo = (grupoId) => {
    setFormData(prev => ({
      ...prev,
      grupos_ids: prev.grupos_ids.includes(grupoId)
        ? prev.grupos_ids.filter(id => id !== grupoId)
        : [...prev.grupos_ids, grupoId]
    }));
  };

  // Validar formulario
  const validarFormulario = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'El nombre de usuario es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'El apellido es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Guardar usuario
  const handleSave = async () => {
    if (!validarFormulario()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      // Preparar datos (enviar solo si tipo_usuario_id tiene valor)
      const dataToSend = {
        ...formData,
        tipo_usuario_id: formData.tipo_usuario_id || null
      };

      await onSave(dataToSend);
      toast.success('Usuario creado exitosamente');
      onClose();
    } catch (error) {
      console.error('Error al crear usuario:', error);

      // Manejar errores de validación del backend
      if (error.details) {
        const backendErrors = {};
        Object.keys(error.details).forEach(key => {
          backendErrors[key] = error.details[key][0]; // Primer error de cada campo
        });
        setErrors(backendErrors);
      }

      toast.error(error.error || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="modal-header-icon">👤</div>
            <div className="modal-header-text">
              <h2 className="modal-header-title">Crear Nuevo Usuario</h2>
              <p className="modal-header-subtitle">El usuario será asignado a tu cuenta</p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Formulario */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Username */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Nombre de usuario <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="usuario123"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: errors.username ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (!errors.username) {
                  e.target.style.borderColor = '#3b82f6';
                }
              }}
              onBlur={(e) => {
                if (!errors.username) {
                  e.target.style.borderColor = '#d1d5db';
                }
              }}
            />
            {errors.username && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                {errors.username}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="usuario@ejemplo.com"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: errors.email ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (!errors.email) {
                  e.target.style.borderColor = '#3b82f6';
                }
              }}
              onBlur={(e) => {
                if (!errors.email) {
                  e.target.style.borderColor = '#d1d5db';
                }
              }}
            />
            {errors.email && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                {errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Contraseña <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Mínimo 6 caracteres"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: errors.password ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (!errors.password) {
                  e.target.style.borderColor = '#3b82f6';
                }
              }}
              onBlur={(e) => {
                if (!errors.password) {
                  e.target.style.borderColor = '#d1d5db';
                }
              }}
            />
            {errors.password && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                {errors.password}
              </p>
            )}
          </div>

          {/* First Name y Last Name en fila */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Nombre <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Juan"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: errors.first_name ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  if (!errors.first_name) {
                    e.target.style.borderColor = '#3b82f6';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.first_name) {
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
              />
              {errors.first_name && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                  {errors.first_name}
                </p>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Apellido <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Pérez"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: errors.last_name ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  if (!errors.last_name) {
                    e.target.style.borderColor = '#3b82f6';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.last_name) {
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
              />
              {errors.last_name && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                  {errors.last_name}
                </p>
              )}
            </div>
          </div>

          {/* Tipo de Usuario */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Rol / Tipo de Usuario
            </label>
            <select
              name="tipo_usuario_id"
              value={formData.tipo_usuario_id}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              <option value="">Sin rol específico</option>
              {tiposUsuario.map(tipo => (
                <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
              ))}
            </select>
          </div>

          {/* Costo Empresa */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Costo Empresa
            </label>
            <input
              type="number"
              name="costo_empresa"
              value={formData.costo_empresa}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
              Costo mensual del usuario para la empresa
            </p>
          </div>

          {/* Grupos de Permisos */}
          <div>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.75rem'
            }}>
              Grupos de Permisos
            </h3>

            {grupos.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#9ca3af',
                backgroundColor: '#f9fafb',
                borderRadius: '0.375rem'
              }}>
                No hay grupos de permisos disponibles
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem'
              }}>
                {grupos.map(grupo => (
                  <label
                    key={grupo.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid',
                      borderColor: formData.grupos_ids.includes(grupo.id) ? '#3b82f6' : '#e5e7eb',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      backgroundColor: formData.grupos_ids.includes(grupo.id) ? '#eff6ff' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.grupos_ids.includes(grupo.id)}
                      onChange={() => handleToggleGrupo(grupo.id)}
                      style={{
                        width: '1rem',
                        height: '1rem',
                        cursor: 'pointer',
                        marginRight: '0.75rem',
                        accentColor: '#3b82f6'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '500',
                        color: '#111827',
                        fontSize: '0.875rem'
                      }}>
                        {grupo.name}
                      </div>
                      {grupo.permissions && grupo.permissions.length > 0 && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6b7280'
                        }}>
                          {grupo.permissions.length} permiso{grupo.permissions.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Stats de grupos seleccionados */}
            {formData.grupos_ids.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#f0f9ff',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                color: '#0369a1',
                border: '1px solid #bae6fd'
              }}>
                <strong>{formData.grupos_ids.length}</strong> grupo{formData.grupos_ids.length !== 1 ? 's' : ''} seleccionado{formData.grupos_ids.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="estado-btn"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="estado-btn"
          >
            {saving ? 'Creando...' : 'Crear Usuario'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GestionUsuarioNuevoModal;
